import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface Pos {
  top: number;
  left: number;
  maxWidth: number;
  maxHeight: number;
}

const GAP = 8;

/**
 * Positions a portaled panel below a trigger button, clamped to the viewport,
 * and closes it on outside click, Escape, scroll or resize. Use this instead
 * of an absolutely-positioned dropdown, which gets clipped by any ancestor
 * with `overflow: hidden` (every `Surface` has it).
 */
export function useAnchoredPopover(open: boolean, close: () => void, width = 260) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Pos | null>(null);

  const place = useCallback(() => {
    const btn = anchorRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const w = Math.min(width, window.innerWidth - GAP * 2);
    const left = Math.min(Math.max(r.left, GAP), window.innerWidth - w - GAP);
    const top = r.bottom + 6;
    setPos({ top, left, maxWidth: w, maxHeight: Math.max(window.innerHeight - top - GAP * 2, 160) });
  }, [width]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!anchorRef.current?.contains(t) && !panelRef.current?.contains(t)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close, place]);

  return { anchorRef, panelRef, pos };
}
